import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getAddressFieldsDataView } from "@insite/client-framework/Store/Data/AddressFields/AddressFieldsSelector";
import { getCurrentCountries } from "@insite/client-framework/Store/Data/Countries/CountriesSelectors";
import translate from "@insite/client-framework/Translate";
import { BillToModel, ShipToModel } from "@insite/client-framework/Types/ApiModels";
import CustomerAddressForm, { CustomerAddressFormStyles } from "@NA/Overrides/Components/CustomerAddressForm";
import { ButtonWrapper } from "@insite/mobius/Button";
import { IconWrapper } from "@insite/mobius/Icon";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import getColor from "@insite/mobius/utilities/getColor";
import React, { useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";

interface OwnProps {
    newAddress: ShipToModel | BillToModel;
    isModalOpen: boolean;
    onModalClose: () => void;
    onFormSubmit: (event: React.FormEvent<HTMLFormElement>, newAddress: ShipToModel | BillToModel) => void;
    extendedStyles?: CreateNewAddressModalStyles;
}

const mapStateToProps = (state: ApplicationState) => ({
    shipToAddressFields: getAddressFieldsDataView(state).value?.shipToAddressFields,
    countries: getCurrentCountries(state),
});

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface CreateNewAddressModalStyles {
    modal?: ModalPresentationProps;
    addressForm?: CustomerAddressFormStyles;
}

export const createNewAddressModalStyles: CreateNewAddressModalStyles = {
    modal: {
        cssOverrides: {
            modalContainer: css`
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            css`
                                padding: 20px 10px;
                            `,
                            null,
                            css`
                                height: 88vh;
                            `,
                            null,
                        ],
                        "max",
                    )}
            `,
            modalBody: css`
                border-radius: 6px;
                border: 1px solid ${getColor("common.border")};
                overflow-y: hidden;
                padding-bottom: 28px;
                box-shadow: none !important;
                max-width: 730px !important;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            css`
                                margin: 0px !important;
                            `,
                            css`
                                height: auto;
                                margin: 45px;
                            `,
                            null,
                            null,
                        ],
                        "max",
                    )}
            `,
            modalTitle: css`
                border: none;
                padding: 18px 28px 10px;
                ${({ theme }) => {
                    return breakpointMediaQueries(
                        theme,
                        [
                            null,
                            css`
                                padding: 18px 20px 10px;
                            `,
                        ],
                        "max",
                    );
                }}
                >${TypographyStyle} {
                    font-size: 28px;
                }
                > ${ButtonWrapper} {
                    &:hover {
                        background: transparent;
                        border-color: transparent;
                    }
                    &:focus {
                        outline: none;
                    }
                    > ${IconWrapper} {
                        color: ${getColor("primary")};
                        width: 35px;
                        height: 35px;
                        > svg {
                            width: 35px;
                            height: 35px;
                            stroke-width: 1px;
                        }
                    }
                }
            `,
            modalContent: css`
                padding: 10px 20px 10px 30px;
                ${({ theme }) => {
                    return breakpointMediaQueries(
                        theme,
                        [
                            null,
                            css`
                                padding: 10px 10px 10px 20px;
                            `,
                        ],
                        "max",
                    );
                }}
            `,
        },
    },
};

const CreateNewAddressModal = ({
    newAddress,
    isModalOpen,
    onModalClose,
    onFormSubmit,
    extendedStyles,
    countries,
    shipToAddressFields,
}: Props) => {
    if (!countries || !shipToAddressFields) {
        return null;
    }

    const [styles] = useState(() => mergeToNew(createNewAddressModalStyles, extendedStyles));
    return (
        <Modal
            {...styles.modal}
            headline={translate("New Address")}
            isOpen={isModalOpen}
            handleClose={onModalClose}
            id="new-address-modal"
            data-test-selector="checkoutShipping_createAddressModal"
        >
            <CustomerAddressForm
                address={newAddress}
                countries={countries}
                addressFieldDisplayCollection={shipToAddressFields}
                onSubmit={onFormSubmit}
                onCancel={onModalClose}
                saveButtonTextOverride={translate("Continue")}
                extendedStyles={styles.addressForm}
                showRemoveInFuture={true}
            />
        </Modal>
    );
};

export default connect(mapStateToProps)(CreateNewAddressModal);
